import {
    Box,
    FormLabel,
    Grid,
    Typography,
    Button,
    debounce,
    DialogContent,
    Stack,
    DialogActions,
    FormControl,
    Checkbox,
    FormControlLabel,
    InputAdornment,
    Dialog,
    Divider,
    Tooltip,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { useEffect, useMemo, useState, ChangeEvent, useRef } from 'react';
import RowTypography from 'components/Table/RowTypography';
import { Link, useNavigate } from 'react-router-dom';
import eventEmitter from 'SnackBarEventEmitter';
import { createDataPipelineProject, deleteDataPipelineProgram, deleteDataPipelineProject, deleteDataPipelineSchedule, editDataPipelineProject, exportProjectListByProgram, getClarityProjectList, getProgramById, getNewProgramSectorList, getProjectListByProgram, getProjectOwnerList } from 'service/service';
import CustomTextField from 'components/Inputs/CustomTextField';
import CustomAutocomplete from 'components/Inputs/CustomAutocomplete';
import { CustomizedDataGrid } from 'components/customized_data_grid';
import WarningImage from '../../assets/rca_generation_warning.svg';
import info from '../../assets/info-icon.svg';
import IdleFilterBlueIcon from '../../assets/filter_idel_blue.svg';
import moment from 'moment';
import ShowInfoWidget from 'components/Widgets/ShowInfoWidget';
import { checkRole } from 'utils/roleUtils';
import { usePepwiseAuthController } from 'Context/contexts/AuthContext';
import { Search } from '@mui/icons-material';
import { useTheme } from "@mui/material/styles";
import { getAutocompleteCommonComponentsProps, getCommonCustomTextFieldProps, getInputCustomCommonSx, getBackgroundSx, getSearchInputLabelProps, searchInputSx, setRdgTableHeight, parseFilter, getProjectCommonColumns, getProjectExportConfig, getPaginationSlice } from './DataPipelineHelper';

const getColumns = ({ isAllowed, programDetail, startDate, handleAction }: { 
    isAllowed: boolean; 
    programDetail: any; 
    startDate: string;
    handleAction: (module: string, action: string, row: any, e: any) => void 
}) => {
    return [
        {
            key: 'Sectors',
            name: 'Sectors',
            formatter(props: any) {
                const sectorList = props.row.Sectors ? props.row.Sectors.split(', ') : [];
                if (sectorList?.length) {
                    return sectorList.map((sector: string, idx: number) => <RowTypography sx={{ backgroundColor: '#39ACE733', color: '#009BD8', padding: '4px 10px', marginRight: '5px', borderRadius: '20px', fontWeight: 600, fontSize: '10px', lineHeight: '16px', display: 'inline-block' }} key={sector} title={sector}>{sector}</RowTypography>);
                } else {
                    return '--'
                }
            },
            minWidth: 150,
            maxWidth: 450
        },
        {
            key: 'Project_Name',
            name: 'Project',
            formatter(props: any) {
                return <Link to={`/data-pipeline/projects/${props.row.Program_ID}/${props.row.Project_Id}/${encodeURIComponent(programDetail?.program_Name)}/${encodeURIComponent(props.row.Project_Name)}?startingDate=${startDate}&endingDate=${startDate}&dateFilter=Today`} state={props.row} title={props.row.Friendly_Name}>{props.row.Project_Name}</Link>
            },
            minWidth: 200,
            maxWidth: 300
        },
        {
            key: 'Project_Owner',
            name: 'Project Owner',
            formatter(props: any) {
                return (
                    <RowTypography textTransform="capitalize">
                        {props.row.Project_Owner || '--'}
                    </RowTypography>
                );
            },
            minWidth: 200,
            maxWidth: 300
        },
        {
            key: 'Project_Description',
            name: 'Description',
            formatter(props: any) {
                return (
                    <RowTypography>
                        {props.row.Project_Description || '--'}
                    </RowTypography>
                );
            },
            minWidth: 200,
            maxWidth: 200
        },
        {
            key: 'Friendly_Name',
            name: 'Alias',
            formatter(props: any) {
                return (
                    <RowTypography>
                        {props.row.Friendly_Name || '--'}
                    </RowTypography>
                );
            },
            minWidth: 100,
        },
        {
            key: 'Created_Dt',
            name: 'Created Date (CST)',
            formatter(props: any) {
                const createdDate = props.row.Created_Dt;
                if (createdDate) {
                    const formattedDate = moment.utc(createdDate).tz("America/Chicago").format('MM-DD-YYYY HH:mm:ss');
                    return (
                        <RowTypography textTransform="capitalize">
                            {formattedDate}
                        </RowTypography>
                    );
                } else {
                    return '--';
                }
            },
            minWidth: 150
        },
        ...getProjectCommonColumns({ isAllowed, handleAction }),
    ];
};

type CanonicalSectorOption = {
  Sector_Id: string;
  Sector_Name: string;
};

interface ProjectQueryParams {
    filterInfo?: any;
    currentFilterType?: string;
    page?: number;
    pageSize?: number;
    stDate?: string;
    edDate?: string;
    sortingInfo?: any;
    sectorId?: string;
    projName?: string;
}

const getProjectOwnerField = ({
	isClarityProject,
	programData,
	formData,
	handleChange,
	handleInputChange,
	ownerList,
	isDisabled,
	dropdownLoading,
	colors
}: any) => {

	const isEdit = programData?.action === "EDIT";

	const customFieldProps71758F = {
		InputLabelProps: {
			sx: {
				'&&': { color: '#71758F !important', fontSize: '14px !important' },
				'&.Mui-focused': { color: '#71758F !important', fontSize: '13px !important' },
				'&.MuiInputLabel-shrink': { color: '#71758F !important', fontSize: '13px !important' }
			}
		},
		InputProps: {
			sx: {
				'&&': { color: '#71758F !important' },
				'& input': { color: '#71758F !important', WebkitTextFillColor: '#71758F !important', fontSize: '14px !important' }
			}
		},
		sx: {
			'& .MuiOutlinedInput-root': {
				backgroundColor: '#F4F7FA !important',
				borderRadius: '4px',
				'& fieldset': { borderColor: 'rgba(113, 117, 143, 0.3) !important' },
				'&:hover fieldset': { borderColor: 'rgba(113, 117, 143, 0.5) !important' },
				'&.Mui-focused fieldset': { borderColor: `${colors.input.borderFocus} !important` }
			},
			'& .MuiInputLabel-root': {
				color: '#71758F !important',
				fontSize: '14px !important'
			},
			'& .MuiInputLabel-root.Mui-focused': {
				color: '#71758F !important',
				fontSize: '13px !important'
			},
			'& .MuiInputLabel-root.MuiInputLabel-shrink': {
				color: '#71758F !important',
				fontSize: '13px !important'
			}
		}
	};

	if (isClarityProject && !isEdit) {
		return (
			<Grid item xs={6}>
                <FormControl fullWidth>
                    <CustomTextField
                        id="ProjectOwner"
                        label="Owner*"
                        name="projectOwner"
                        variant="outlined"
                        autoComplete="off"
                        value={formData.projectOwner}
                        disabled
                        required
                        onChange={handleChange}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: '#F4F7FA !important',
                                borderRadius: '4px',
                                '& fieldset': { borderColor: 'rgba(113, 117, 143, 0.3) !important' }
                            },
                            '& .MuiInputBase-input': {
                                color: '#71758F !important',
                                WebkitTextFillColor: '#71758F !important',
                                fontSize: '14px !important'
                            },
                            '& .MuiInputLabel-root': {
                                color: '#71758F !important',
                                fontSize: '14px !important'
                            },
                            '& .MuiInputLabel-root.Mui-focused': {
                                color: '#71758F !important',
                                fontSize: '13px !important'
                            },
                            '& .MuiInputLabel-root.MuiInputLabel-shrink': {
                                color: '#71758F !important',
                                fontSize: '13px !important'
                            },
                            '& .MuiFormHelperText-root': {
                                color: '#71758F !important'
                            }
                        }}
                    />
                </FormControl>
            </Grid>
		);
	}

	return (
		<Grid item xs={6}>
        <FormControl fullWidth>
            <CustomAutocomplete
                loading={dropdownLoading.owner}
                options={ownerList}
                noOptionsText="No user found"
                value={formData.projectOwner}
                onChange={(e, val) => handleChange(e, val, 'projectOwner')}
                onInputChange={(e, val) => handleInputChange(e, 'projectOwner', val)}
                disabled={
                    isDisabled.owner || (isClarityProject && programData.action == 'EDIT')
                }
                textFieldLabel="Owner*"
                sx={{
                    ...getInputCustomCommonSx(colors),
                    '& .MuiInputBase-input': { color: '#71758F !important', WebkitTextFillColor: '#71758F !important' },
                    '& .MuiInputLabel-root': { color: '#71758F !important' },
                    '& .MuiFormLabel-root': { color: '#71758F !important' },
                    '& .MuiAutocomplete-input': { color: '#71758F !important', WebkitTextFillColor: '#71758F !important' }
                }}
                componentsProps={getAutocompleteCommonComponentsProps(colors)}
                customTextFieldProps={customFieldProps71758F}
            />
        </FormControl>
    </Grid>
	);
};

const SECTOR_MAPPING_DETAILS = [
    {
        sector: 'International Beverages',
        legacySectors:
            'EUROPE, AMESA, LATAM, and APAC Franchise Beverage Units'
    },
    {
        sector: 'North America',
        legacySectors:
            'PBNA, PFNA, PQGC'
    },
    {
        sector: 'EMEA Foods',
        legacySectors:
            'EUROPE, AMESA'
    },
    {
        sector: 'LATAM Foods',
        legacySectors:
            'LATAM'
    },
    {
        sector: 'APAC Foods',
        legacySectors:
            'APAC'
    },
    {
        sector: 'Global',
        legacySectors:
            'GLOBAL'
    },
    {
        sector: 'Corporate',
        legacySectors:
            'CORPORATE'
    }
];

const sectorMappingTooltipContent = (
    <Box
        sx={{
            minWidth: '280px',
            maxWidth: '360px'
        }}
    >

        <Stack spacing={0.75}>
            {SECTOR_MAPPING_DETAILS.map(
                ({ sector, legacySectors }) => (
                    <Box
                        key={sector}
                        sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '6px'
                        }}
                    >
                        <Box
                            component="span"
                            aria-hidden="true"
                            sx={{
                                lineHeight: 1.5,
                                flexShrink: 0,
                                color: '#000000 !important'
                            }}
                        >
                            •
                        </Box>

                        <Typography
                            component="div"
                            variant="body2"
                            sx={{
                                fontFamily: 'Montserrat',
                                fontSize: '12px',
                                lineHeight: 1.5,
                                color: '#000000 !important',
                                opacity: 1
                            }}
                        >
                            <Box
                                component="span"
                                sx={{
                                    fontWeight: 700
                                }}
                            >
                                {sector}:
                            </Box>{' '}
                            {legacySectors}
                        </Typography>
                    </Box>
                )
            )}
        </Stack>
    </Box>
);

export const ProjectTab = ({ handleEditClose, programData, programID, programName, setProjectData }: any) => {
    const theme = useTheme();
    const colors = theme.palette;
    useEffect(() => {
        document.body.classList.add("v2Background");
    },);
    const [sectorList, setSectorList] = useState<CanonicalSectorOption[]>([]);
    const [loading, setLoading] = useState<any>({
        sector: false,
        project: false
    });
    const [saveBtnLoader, setSaveBtnLoader] = useState(false);
    const [isDisabled, setIsDisabled] = useState<any>({
        description: false,
        owner: false
    });
    const [inputValue, setInputValue] = useState('');
    const [ownerList, setOwnerList] = useState<any>([]);
    const [isClarityProject, setIsClarityProject] = useState(false);
    const [validSlaTime, setValidSlaTime] = useState(true);
    const [projectList, setProjectList] = useState<any>([]);
    const [projectListForm, setProjectListForm] = useState([]);
    const [open, setOpen] = useState(false);
    const isSlaChanged = false;
    type ProjectOption = {
        label: string;
        id: string;
    };
    const [projectOptionList, setProjectOptionList] = useState<ProjectOption[]>([]);
    const [openModal, setOpenModal] = useState<any>({ isOpen: false, message: '' });

    const isUpdatedFlag = useRef(false);
    const isInitialized = useRef(false);
    const formContainerRef = useRef<HTMLDivElement>(null);
    const [sectorName, setSectorName] = useState('all');
    const startDate = moment().tz("America/Chicago").startOf('day').format('YYYY-MM-DD');
    const endDate = moment().tz("America/Chicago").endOf('day').format('YYYY-MM-DD');
    const [tableLoading, setTableLoading] = useState(false);
    const [paginationData, setPaginationData] = useState({ totalElements: 0, totalPages: 0, currentPage: 0 });
    const [filterData, setFilterData] = useState<any[]>([]);
    const [sortingInfo, setSortingInfo] = useState({ sortBy: '', sortDir: '' });
    const [filterInfo, setFilterInfo] = useState([]);
    const currentFilterType = '';
    const sectorMasterList: any[] = [];
    const [searchProject, setSearchProject] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(100);
    const [projectsDataList, setProjectsDataList] = useState<any[]>([]);
    const [programDetail, setProgramDetail] = useState<any>({});
    const [controller] = usePepwiseAuthController();
    const { role } = controller;
    const isAllowed = checkRole(role, ['Data Operations Admin', 'PepWise System User']);
    const searchParams = new URLSearchParams(location.search);
    const type = searchParams.get('type');
    const [dropdownLoading, setDropdownLoading] = useState<any>({
        project: false,
        owner: false
    });

    const [formData, setFormData] = useState<any>({
        id: '',
        Sectors: [],
        ProgramName: '',
        projectName: '',
        projectDescription: '',
        projectOwner: '',
        clarityId: '',
        slaDate: 0,
        slaTime: '',
        reqSlaTime: '',
        friendlyName: '',
        isSlaConfigured: false,
        slaType: '',
        slaData: []
    });

    // table data 
    const tableData: any[] = [];
    const defaultColumnNames = ['Sectors', 'Project', 'Project Owner', 'Description', 'Alias', 'Created Date (CST)', 'Failed', 'Success', 'SLA Status', 'SLA Time (CST)', 'Actual Completion Time (CST)', 'Action'];

    const queryParams = new URLSearchParams(location.search);
    const isEdited = queryParams.get('isEdited') === 'true';
    const projectIdFromURL = queryParams.get('projectID');
    const [toast, setToast] = useState<any>({
        open: false,
        message: ''
    });

    const handleThreeDotsClose = (event: any) => {
        event.preventDefault();
        event.stopPropagation();
    };

    const handleDetailsThreeDotsClose = (event: any) => {
        event.preventDefault();
        event.stopPropagation();
    };

    const buildSlaTime = (value: number | null | undefined): string => {
        if (value == null) return '';
        let reqslatime = '';
         if (value > 0) {
            if (value < 10) {
                reqslatime = `000${value}`.replace(/(.{2})$/, ':$1');
            } else if (value < 100) {
                reqslatime = `00${value}`.replace(/(.{2})$/, ':$1');
            } else {
                reqslatime = value.toString().replace(/(.{2})$/, ':$1');
            }
        } else if (value === 0) {
            reqslatime = '00:00';
        } else {
            reqslatime = 'NA';
        }
        return reqslatime;
    };

    const commonInputSx = {
        '& .MuiOutlinedInput-root': {
            backgroundColor: '#F4F7FA !important',
            borderRadius: '4px',
            '& fieldset': {
                borderColor: 'rgba(113, 117, 143, 0.3) !important',
                borderWidth: '1px !important'
            },
            '&:hover fieldset': {
                borderColor: 'rgba(113, 117, 143, 0.5) !important'
            },
            '&.Mui-focused fieldset': {
                borderColor: `${colors.input.borderFocus} !important`
            }
        },
        '& .MuiInputBase-input': {
            color: '#71758F !important',
            WebkitTextFillColor: '#71758F !important',
            fontSize: '14px !important'
        },
        '& .MuiInputLabel-root': {
            color: '#71758F !important',
            fontSize: '14px !important'
        },
        '& .MuiInputLabel-root.Mui-focused': {
            color: '#71758F !important',
            fontSize: '13px !important'
        },
        '& .MuiInputLabel-root.MuiInputLabel-shrink': {
            color: '#71758F !important',
            fontSize: '13px !important'
        },
        '& .MuiFormHelperText-root': {
            color: '#71758F !important'
        }
    };

    const scrollToTop = () => {
        setTimeout(() => {
            formContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
    };

    const handleAction = (module: string, type: string, data: any, e: any) => {
        if (e?.preventDefault) {
            e.preventDefault();
            e.stopPropagation();
            handleThreeDotsClose(e);
            handleDetailsThreeDotsClose(e);
        }
        data = {
            ...data,
            ProgramName: programDetail?.program_Name,
            ProgramID: data.Program_ID
        };
        let reqslatime = '';
        if (type === 'edit') {
            let slaTimeValue = programData.data?.slaTime;
            let SLA_TimeValue = programData.data?.SLA_Time;
            const rawValue = slaTimeValue ?? SLA_TimeValue;
            reqslatime = buildSlaTime(rawValue);
        }

        switch (type) {
            case 'edit':
                programData.action = 'EDIT';
                setOpenModal({
                    ...openModal,
                    isOpen: false,
                    type: module,
                    action: 'EDIT',
                    data: data
                });
                setFormData({
                    projectName: data.Project_Name,
                    projectDescription: data.Project_Description,
                    projectOwner: data.Project_Owner,
                    projectId: data.Project_Id,
                    Sectors: data.Sectors ? data.Sectors.split(', ') : [],
                    friendlyName: data.Friendly_Name,
                    slaTime: data?.slaType == 1 ? null : `2024-07-21T${reqslatime}`,
                    slaDate: data?.slaType == 1 ? 0 : data?.dateValue || 0,
                    reqSlaTime: data?.slaType == 1 ? null : data?.SLA_Time,
                    slaType: data?.slaType == 1 ? 'weekly' : 'daily',
                    isSlaConfigured: data?.Is_Sla_Configured == 1
                });
                setIsClarityProject(!!data.Clarity_Project_Id);
                // Scroll to form on edit
                scrollToTop();
                break;
            case 'delete':
                programData.action = 'DELETE';
                programData.isOpen = true;
                programData.data = data;
                setOpenModal({
                    ...openModal,
                    isOpen: true,
                    type: module,
                    action: 'DELETE',
                    data: data
                });
                break;
            case 'view':
                window.location.href = `/data-pipeline/projects/${data.Program_ID}/${data.Project_Id}/${encodeURIComponent(
                    data.ProgramName
                )}/${encodeURIComponent(data.Project_Name)}`;
                break;
            default:
                break;
        }
    };

    const navigate = useNavigate();

    eventEmitter.on('showSnackbar', (message: string) => {
        setLoading(false);
        setDropdownLoading({
            project: false,
            owner: false
        });
    });

    const debouncedFetchOptionsProjectSearch = useMemo(
        () =>
            debounce(async (value) => {
                getProjectsByProgram({filterInfo: parseFilter(filterInfo), currentFilterType, page: 1, pageSize: 100, stDate: startDate, edDate: endDate, sortingInfo: { sortBy: '', sortDir: '' }, sectorId: getSectorId(), projName: searchProject || value});
            }, 500),
        []
    );

    const debouncedFetchOptions = useMemo(
        () =>
            debounce(async (value, type) => {
                setLoading({
                    ...loading,
                    project: true
                });
                if (type === 'projectName') {
                    getProjectList(value);
                } else {
                    getOwnerList(value);
                }
                setLoading({
                    ...loading,
                    project: false
                });
            }, 500),
        []
    );

    useEffect(() => {
        if (!isInitialized.current) {
            isInitialized.current = true;
            resetAndRefresh();
        }
    }, []);

    useEffect(() => {
        if ((programData?.type?.toLowerCase() === 'project' || type?.toLowerCase() === 'project') && inputValue.length > 2) {
            debouncedFetchOptions(inputValue, 'projectName');
        }
        let sectorId;
        if (sectorName && sectorName != 'all' && sectorMasterList?.length > 0) {
            sectorId = sectorMasterList.filter((s: any) => s.Sector_Name == sectorName)[0].Sector_Id
        }
        setSearchProject('');
    }, [inputValue, debouncedFetchOptions]);

    const getSectorId = () => {
        let sectorId;
        let selectedSctr = localStorage.getItem('selectedSector')
        if (selectedSctr && selectedSctr != 'all') {
            setSectorName(selectedSctr)
            const sectorList = JSON.parse(localStorage.getItem('sectorsMasterData') ?? '')
            if (sectorList) {
                sectorId = sectorList.filter((s: any) => s.Sector_Name == selectedSctr)[0].Sector_Id
            }
        }
        return sectorId;
    }

    const getProjectsByProgram = async ({
        filterInfo,
        currentFilterType,
        page,
        pageSize,
        stDate,
        edDate,
        sortingInfo,
        sectorId,
        projName
    }: ProjectQueryParams = {}) => {
        setTableLoading(true);
        stDate = stDate || startDate
        edDate = edDate || endDate
        sectorId = sectorId || getSectorId()
        const fetchPageNumber = 1;
		const fetchPageSize = 10000;
        let result = await getProjectListByProgram(programID, fetchPageNumber, fetchPageSize, currentFilterType, filterInfo, stDate, edDate, sortingInfo, sectorId, projName || '');
        setTimeout(() => {
            setRdgTableHeight(
                `project_list${programID}`,
                result.data.projectList.length
            );
        }, 100);
        setTableLoading(false);
        const projData = result?.data?.projectList?.map((list: any) => {
            list['activeRow'] = 0;
            const selectedSectors = list.Sectors?.split(', ');
            list['Sectors'] = selectedSectors?.length ? list.Sectors : "";
            return list;
        });
        setProjectsDataList(projData);
		// Set pagination info (client side)
		const totalElements = projData.length;
  		const totalPages = Math.ceil(totalElements / Number(pageSize || 100));
		setPaginationData({
			totalElements,
			totalPages,
			currentPage: 1
		});
		setPage(1);
  		setProjectList(projData.slice(0, Number(pageSize)));
        const filtersData: any = {
            Sectors: [], Project_Name: [], SLA_Status: []
        }
        if (result.data.filterOptions) {
            const entries = Object.entries(result.data.filterOptions);
            for (const [key, value] of entries) {
                if (key === 'sector' || key === 'Sector_Name') {
                    filtersData['Sectors'] = value;
                } 
                else if (key === 'Project_Name' || key === 'projectName') {
                    filtersData['Project_Name'] = value;
                } 
                else if (key === 'SLA_Status' || key === 'projectSlaStatus') {
                    filtersData['SLA_Status'] = (value as string[]).filter(Boolean);
                } 
                else if (key === 'Project_Owner' || key === 'projectOwner') {
                    filtersData['Project_Owner'] = value;
                } 
                else if (key === 'Created_Dt' || key === 'createdDate') {
                    filtersData['Created_Dt'] = value;
                }
            }
        }
        setFilterData(filtersData);
        setFilterInfo(filterInfo);
        setProjectData(projData);
        if (projectIdFromURL && projData.length > 0) {
            const matchingRow = projData.find((row: any) =>
                row.Project_Id?.toString() === projectIdFromURL
            );
            if (matchingRow && !isUpdatedFlag.current) {
                handleAction('Project', 'edit', matchingRow, null);
            }
            isUpdatedFlag.current = false;
        }
    };

    const getOwnerList = async (name = '') => {
        setDropdownLoading({
            ...dropdownLoading,
            owner: true
        });
        let result = await getProjectOwnerList(name);
        setDropdownLoading({
            ...dropdownLoading,
            owner: false
        });
        let owner: any = [];
        result.data.projectOwnerNames.map((obj: string) => {
            let formattedData = (obj.includes('null') ? obj.replace('(null)', '') : obj).trim();
            owner.push(formattedData);
        });
        setOwnerList(owner);
    };

    const getProjectList = async (name = '') => {
        setDropdownLoading({
            ...dropdownLoading,
            project: true
        });
        let result = await getClarityProjectList(name);
        setDropdownLoading({
            ...dropdownLoading,
            project: false
        });
        setProjectListForm(result.data.projectList);
        setProjectOptionList(
            result.data.projectList.map((obj: any) => {
                return { label: obj.Project_Name, id: obj.Project_Code };
            })
        );
    };

    const handleInputChange = (event: any, name: any, newInputValue: any) => {
        if (name === 'projectName') {
            setInputValue(newInputValue);
            setFormData({
                ...formData,
                [name]: newInputValue
            });
        } else if (name === 'projectOwner') {
            setFormData({
                ...formData,
                [name]: newInputValue
            });
            if (event?.type == 'change') {
                debouncedFetchOptions(newInputValue, name);
            }
        } else {
            setFormData({
                ...formData,
                [name]: newInputValue
            });
        }
    };

    const getProgramDetails = async (sectorId?: string, date?: string) => {
        setLoading(true);
        let result = await getProgramById(programID, sectorId, date || startDate);
        setLoading(false);
        setProgramDetail(result.data?.programData ? result.data.programData[0] : '');
    };

    const getSectorData = async () => {
  setLoading(
    (previous: any) => ({
      ...previous,
      sector: true
    })
  );

  try {
    const response: any =
      await getNewProgramSectorList();

    const rawSectorOptions =
      response?.data?.data
        ?.sectorOptions ??
      response?.data?.sectorOptions ??
      [];

    const sectorOptions:
      CanonicalSectorOption[] =
      Array.isArray(
        rawSectorOptions
      )
        ? rawSectorOptions
            .filter(
              (
                sector
              ): sector is CanonicalSectorOption =>
                sector !== null &&
                typeof sector ===
                  'object' &&
                typeof sector.Sector_Id ===
                  'string' &&
                typeof sector.Sector_Name ===
                  'string'
            )
            .map((sector) => ({
              Sector_Id:
                sector.Sector_Id.trim(),
              Sector_Name:
                sector.Sector_Name.trim()
            }))
            .filter(
              (sector) =>
                Boolean(
                  sector.Sector_Id &&
                  sector.Sector_Name
                )
            )
        : [];

    setSectorList(sectorOptions);
  } catch (error) {
    console.error(
      'Failed to load canonical sector options:',
      error
    );

    setSectorList([]);
  } finally {
    setLoading(
      (previous: any) => ({
        ...previous,
        sector: false
      })
    );
  }
};

    const editProject = async (slaDataChange?: boolean) => {
        let data = {
            id: formData.projectId,
            projectOwner: formData.projectOwner,
            projectName: formData.projectName,
            projectDescription: formData.projectDescription,
            mulSector: formData.Sectors,
            friendlyName: formData.friendlyName,
            isSlaConfigured: formData?.isSlaConfigured,
            slaType: (() => {
                if (formData?.slaType == 'weekly') return 1;
                if (formData?.slaType == 'daily') return 0;
                return '';
            })(),
            slaData: formData?.slaType == 'daily' ? [] : tableData,
            slaDate: formData.slaDate,
            slaTime: formData.reqSlaTime,
            isSlaChanged: slaDataChange || isSlaChanged
        };
        let sectorIds: string[] = [];
        for (const sector of sectorList) {
            if (formData.Sectors.includes(sector.Sector_Name)) {
                sectorIds.push(sector.Sector_Id);
            }
        }
        data['mulSector'] = sectorIds;
        await editDataPipelineProject(data);
        setFormData(initialFormData);
    };


    const createProject = async (data: any) => {
        let result = await createDataPipelineProject(data);

        setSaveBtnLoader(false);
        return result;
    };

    const validateAliasInput = (event: any) => {
        const allowedChars = /^[a-zA-Z0-9\s_-]*$/;
        if (!allowedChars.test(event.key)) {
            event.preventDefault();
        }
    };

    const handlePaste = (event: any) => {
        event.preventDefault();
    };

    const getToastData = (data: any) => {
        setToast({
            ...toast,
            message: data.data.message
        });
    };

    const handleCreateAction = async (
        formData: any,
        ownerList: any[],
        isClarityProject: boolean,
        tableData: any[],
        sectorList: any[]
    ) => {
        let index = ownerList.indexOf(formData.projectOwner);

        let formPayload: any = {
            programID: programID,
            projectName: formData?.projectName?.label || formData?.projectName,
            clarityProId: formData?.clarityId || '',
            projectDescription: formData?.projectDescription,
            projectOwner: index >= 0 || isClarityProject ? formData?.projectOwner : '',
            mulSector: formData?.Sectors,
            friendlyName: formData.friendlyName,
            isSlaConfigured: formData?.isSlaConfigured,
            slaType: (() => {
                if (formData?.slaType == 'weekly') return 1;
                if (formData?.slaType == 'daily') return 0;
                return '';
            })(),
            slaData: formData?.slaType == 'daily' ? [] : tableData,
            slaDate: formData.slaDate,
            slaTime: formData.reqSlaTime
        };

       let sectorIds: string[] = [];
        for (const sector of sectorList) {
            if (formData.Sectors.includes(sector.Sector_Name)) {
                sectorIds.push(sector.Sector_Id);
            }
        }
        formPayload['mulSector'] = sectorIds;
        let result = await createProject(formPayload);

        if (result.data?.message) {
            const createdProjectId = result.data.projectId;
            formData.projectId = createdProjectId;
            getToastData(result);
        } else if (result.response?.status == 400) {
            getToastData({ data: { message: result.response.data.data.message } });
        }

        return result;
    };

    const handleEditAction = async (slaDataChange?: boolean) => {
        await editProject(slaDataChange);
    };

    const handleDeleteAction = async (programData: any) => {
        if (programData.type === 'Program') {
            return await deleteDataPipelineProgram(
                programData.data.Program_ID || programData.data.Program_id
            );
        }

        if (programData.type === 'Project') {
            return await deleteDataPipelineProject(programData.data.Project_Id);
        }

        // Delete schedules
        const jobIds = programData.data.map((d: any) => d.jobId);

        const reqBody = {
            bulkProjectId: programData.data[0].Project_Id,
            bulkJobId: jobIds,
            scheduler: programData.data[0].schedulerName
        };

        return await deleteDataPipelineSchedule(reqBody);
    };

    const resetAndRefresh = () => {
        getProjectList();
        programData.action = 'CREATE';
        programData.isOpen = false;
        programData.type = 'Project';
        getOwnerList('');
        getSectorData();

        let sectorId;
        if (sectorName && sectorName != 'all' && sectorMasterList?.length > 0) {
            sectorId = sectorMasterList.filter((s: any) => s.Sector_Name == sectorName)[0].Sector_Id
        }
        getProgramDetails(sectorId);
        getProjectsByProgram({filterInfo: parseFilter(filterInfo), currentFilterType, page: 1, pageSize: 100, stDate: startDate, edDate: endDate, sortingInfo, sectorId});
    };

    const onSubmit = async (event: any, isforceUpdate?: boolean, slaDataChange?: boolean) => {

        if (saveBtnLoader) return;
        setSaveBtnLoader(true);
        event.preventDefault();

        switch (programData.action) {
            case 'CREATE':
                await handleCreateAction(
                    formData,
                    ownerList,
                    isClarityProject,
                    tableData,
                    sectorList
                );
                break;

            case 'EDIT':
                await handleEditAction(slaDataChange);
                break;

            case 'DELETE':
                await handleDeleteAction(programData);
                break;
        }

        setSaveBtnLoader(false);

        handleClose('updated');

        isUpdatedFlag.current = true;
        resetAndRefresh();
        if (isEdited)
            navigate(`/data-pipeline/programs-tab?programID=${programID}&programName=${programName}&tab=1&type=Project&projectName=${formData?.projectName}&projectId=${formData?.projectId}&isEdited=true`);
        else
            navigate(`/data-pipeline/programs-tab?programID=${programID}&programName=${programName}&tab=1&type=Project&projectName=${formData?.projectName}&projectId=${formData?.projectId}`);
        setFormData(initialFormData);
    };

    const isProjectFormValid = () => {
        return (
            formData.projectName &&
            formData.projectName?.length <= 100 &&
            formData.projectDescription &&
            formData.projectOwner &&
            formData.Sectors &&
            formData.Sectors?.length &&
            formData.friendlyName?.length <= 100 &&
            validSlaTime
            &&
            (isClarityProject || true)
        );
    };

    const handleOnBlur = (event: any, type: string) => {
        if (type === 'friendlyName') {
            setFormData({
                ...formData,
                [type]: event.target.value.trim()
            });
        }
    };
    const handleChange = (event: any, newValue: any = '', type: string = '') => {
        if (projectOptionList.length && newValue?.id) {
            setIsClarityProject(true)
        }
        if (newValue === null) {
            setIsDisabled({
                description: false,
                owner: false
            });
            if (!isClarityProject && type === 'projectName') {
                setFormData({
                    ...formData,
                    projectName: '',
                    projectOwner: '',
                    projectDescription: ''
                });
            }

            return;
        }
        if (!type) {
            const { name, value } =
                event && event?.target?.type === 'checkbox'
                    ? { name: event.target.name, value: event.target.checked }
                    : event?.target || {};
            setFormData({
                ...formData,
                [name]: value
            });
            return;
        }

        const handleProjectName = (newValue: any) => {
            setIsDisabled({ description: false, owner: false });
            if (!newValue?.id) return;
            
            const data: any = projectListForm.filter((obj: any) => obj.Project_Code === newValue.id);
            setFormData({
                ...formData,
                projectDescription: data[0]?.Project_Description || '',
                projectOwner: data[0]?.EMPLOYEE_NAME,
                projectName: data[0]?.Project_Name,
                clarityId: data[0]?.Project_Code
            });
            setIsDisabled({ description: true, owner: true });
        };

        const handleSectors = (checked: boolean, newValue: any) => {
            const selectedSectors = [...formData.Sectors];
            if (checked) {
                selectedSectors.push(newValue);
            } else {
                selectedSectors.splice(selectedSectors.indexOf(newValue), 1);
            }
            setFormData({ ...formData, Sectors: selectedSectors });
        };

        const handleSlaTime = (event: any) => {
            const dateObj = new Date(event);
            const hours = dateObj.getHours().toString().padStart(2, '0');
            const minutes = dateObj.getMinutes().toString().padStart(2, '0');
            const isValid = !isNaN(Number(hours)) && !isNaN(Number(minutes));
            
            setValidSlaTime(isValid);
            
            if (Number(hours) >= 0 && Number(minutes) >= 0) {
                const formattedDate = dateObj?.toISOString().split('T')[0];
                const reqTime = Number(`${hours}${minutes}`);
                const time = `${formattedDate}T${hours}:${minutes}`;
                setFormData({ ...formData, slaTime: time, reqSlaTime: reqTime });
            } else {
                setFormData({ ...formData, slaTime: null, reqSlaTime: null });
            }
        };

        const typeHandlers: { [key: string]: (newValue?: any) => void } = {
            projectName: () => handleProjectName(newValue),
            projectOwner: () => setFormData({ ...formData, projectOwner: newValue || '' }),
            projectDescription: () => setFormData({ ...formData, projectDescription: '' }),
            Sectors: () => handleSectors(event.target.checked, newValue),
            slaTime: () => handleSlaTime(event),
            friendlyName: () => setFormData({ ...formData, friendlyName: event.target.value }),
            slaDate: () => setFormData({ ...formData, slaDate: event.target.value })
        };

        const handler = typeHandlers[type];
        handler?.();
    };

    const initialFormData = {
        Sectors: [],
        projectName: '',
        projectDescription: '',
        projectOwner: '',
        friendlyName: '',
    };

    const handleCancl = (type: string) => {
        setIsClarityProject(false);
        setFormData(initialFormData);
        programData.action = 'CREATE'
        handleEditClose(type);
        getProjectList();
    };
    const handleClose = (type: string) => {
        handleEditClose(type);
        setOpenModal({
            isOpen: false
        });
    };

    const onCreatenewPrj = (e: any) => {
        const { checked } = e.target;
        setOpen(false)
        setIsClarityProject(false)
        setFormData({
            ...formData,
            projectName: formData.projectName || '',
            projectOwner: '',
            projectDescription: ''
        });
        if (checked) {
            setIsDisabled({
                description: false,
                owner: false
            });
        } else {
            setIsDisabled({
                description: true,
                owner: true
            });
        }
    };

    const getLabel = () => {
        return (
            <Box>
                Project Name{' '}
                <Box component="span" sx={{ fontStyle: 'italic' }}>
                    (Search & Select from Clarity)*
                </Box>
            </Box>
        );
    };
    const handleSorting = (sortingInfo: any) => {

        getProjectsByProgram({filterInfo: parseFilter(filterInfo), currentFilterType, page, pageSize, stDate: startDate, edDate: endDate, sortingInfo, sectorId: getSectorId()});
        setSortingInfo(sortingInfo)
    };

    const handleFilter = (filterInfo: any) => {
        setFilterInfo(filterInfo);
        getProjectsByProgram({filterInfo: parseFilter(filterInfo), currentFilterType, page, pageSize, stDate: startDate, edDate: endDate, sortingInfo, sectorId: getSectorId()});
    };

    const handleSearch = (e: any) => {
        console.log('FILTER', e);
    };

    // export
    const exportGetProjectsByProgram = async (filterInfo?: any, currentFilterType?: string, stDate?: string, edDate?: string, sortingInfo?: any, sectorId?: string, projName?: string) => {
        stDate = stDate || startDate
        edDate = edDate || endDate
        const payload = getProjectExportConfig({
			filterInfo,
			currentFilterType,
    		stDate,
    		edDate,
    		sortingInfo,
    		sectorId,
    		projName,
    		programID,
    		programName
  		});

  		await exportProjectListByProgram(payload);
    };

    const handlePageChange = (event: React.ChangeEvent<unknown>, newPage: number) => {
		setPage(newPage);
		
      const data = getPaginationSlice({
        list: projectsDataList,
        newPage,
        pageSize,
      });

      setProjectList(data);
    };
	
	const handlepageSizeChange = (event: any) => {
		const newPageSize = Number(event.target.value);
  		setPageSize(newPageSize);
  		setPage(1);
  		const data = projectsDataList.slice(0, newPageSize);
  		setProjectList(data);
  		setPaginationData({
			...paginationData,
    		totalPages: Math.ceil(projectsDataList.length / newPageSize),
    		totalElements: projectsDataList.length,
    		currentPage: 1
  		});
	};

    const onCreateProgramClick = (event: any) => {
        setOpenModal({
            ...openModal,
            isOpen: true,
            type: 'Project',
            action: 'CREATE'
        });
    };

    const onProjectSearch = (value: string) => {
        setSearchProject(value)
        debouncedFetchOptionsProjectSearch(value)
    }

    let deleteMessage;
    if (programData.type === "Program") {
        deleteMessage = `this ${programData.type}?`;
    } 
    else if (programData.type === "Project") {
        deleteMessage = (
            <Box component="span">
                this project from{' '}
                <Box component="span" sx={{ fontWeight: '800' }}>
                    {programName}
                </Box>
                ?
            </Box>
        );
    } 
    else if (programData?.data && programData.data.length > 1) {
        deleteMessage = `${programData.data.length} jobs from the project?`;
    } 
    else {
        deleteMessage = " this job from the project?";
    }

    let content;
    if (programDetail?.ProjectCount > 0 && !tableLoading) {
        content = (
            <Box sx={{ position: 'relative' }}>
                {projectList?.length > 0 ? (
                    <Box>
                        <CustomizedDataGrid
                            tableColumns={getColumns({ isAllowed, programDetail, startDate, handleAction })}
                            tableData={projectList}
                            tableType="program-list"
                            idleFilterIcon={IdleFilterBlueIcon}
                            defaultColumnNames={defaultColumnNames}
                            onSorting={handleSorting}
                            currSorting={sortingInfo}
                            filterDataOptions={filterData}
                            onFilterCheck={handleFilter}
                            currFilter={filterInfo}
                            onFilterSearch={handleSearch}
                            page={page}
                            pageSize={pageSize}
                            paginationData={paginationData}
                            listLength={projectsDataList.length}
                            handlepageSizeChange={handlepageSizeChange}
                            handlePageChange={handlePageChange}
                            isPrevNextPagination
                            isFetching={tableLoading}
                            showExport={true}
                            fileName={`programs_Page_${paginationData.currentPage}_of_${paginationData.totalPages}.xlsx`}
                            // isShowDisabledButton={false}
                            exportTableData={() =>
                                exportGetProjectsByProgram(
                                    parseFilter(filterInfo),
                                    currentFilterType,
                                    startDate,
                                    endDate,
                                    sortingInfo,
                                    getSectorId(),
                                    ''
                                )
                            }
                            isDateRangeFilter={false}
                            isDisabled={false}
                            isShowMoreButton
                        />
                    </Box>
                ) : (
                    <Stack justifyContent="center" alignItems="center" height="60vh">
                        <Typography variant="pageTitle" color="dark.main">
                            No data for the selected filters
                        </Typography>
                    </Stack>
                )}
            </Box>
        );
    } else if (tableLoading) {
        content = (
            <Stack justifyContent="center" alignItems="center" height="60vh">
                <Typography variant="pageTitle" color="dark.main">
                    Loading...
                </Typography>
            </Stack>
        );
    } else {
        content = (
            <ShowInfoWidget
                title="No projects have been added yet"
                height="50vh"
                button=""
                onClick={onCreateProgramClick}
            />
        );
    }

    return (
			<Box ref={formContainerRef} className={'main-bordered-container'} sx={{ mt: 1, padding: '10px', borderRadius: '8px' }}>
				<Typography variant="body2" sx={{ opacity: 0.7, mb: 2, px: 2, color: 'textLight.main',ml:2.5 }}>
					<img src={info} alt="" /> This section is to configure project.
				</Typography>

				<Grid container spacing={2}>
					<Grid item xs={10}>
						<Box component="form" onSubmit={onSubmit} noValidate p={2}>
							<DialogContent>
								<Grid container spacing={2}>
									<Grid item xs={12}>
										<Box
											sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '16px' }}
										>
											<Box 
                                                sx={{
                                                    position: 'absolute',
                                                    zIndex:2,
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '5px',
                                                    pr: '14px',
                                                    backgroundColor:
                                                        colors.backgroundLight?.primary ?? '#FFFFFF'
                                                }}
                                            >
                                                <Typography
                                                    variant="subHeader"
                                                    color="dark.main"
                                                    sx={{
                                                        color: '#009BD8',
                                                        fontWeight: 600,
                                                        lineHeight: 1
                                                    }}
                                                >
                                                    Select Sectors
                                                </Typography>
                                                <Tooltip
                                                    arrow
                                                    placement="top"
                                                    title={sectorMappingTooltipContent}
                                                    slotProps={{
                                                        tooltip: {
                                                            sx: {
                                                                backgroundColor:
                                                                    `${colors.dataPipeline?.accordionWidget?.accordionHeader?.tooltipBackground
                                                                        ??
                                                                        '#FFFFFF'} !important`,
                                                                color:
                                                                    colors.dataPipeline?.accordionWidget
                                                                        ?.accordionHeader
                                                                        ?.tooltipText ??
                                                                        '#000000',
                                                                boxShadow:
                                                                    '0px 4px 12px rgba(0, 0, 0, 0.15)',
                                                                borderRadius: '8px',
                                                                padding: '12px 14px',
                                                                maxWidth: '380px'
                                                            }
                                                        },
                                                        arrow: {
                                                            sx: {
                                                                color:
                                                                    colors.dataPipeline?.accordionWidget
                                                                        ?.accordionHeader
                                                                        ?.tooltipArrow ??
                                                                    '#FFFFFF'
                                                            }
                                                        }
                                                    }}
                                                >
                                                    <Box
                                                        component="span"
                                                        role="button"
                                                        tabIndex={0}
                                                        aria-label="View sector mapping updates"
                                                        sx={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            cursor: 'help',
                                                            color: '#009BD8',
                                                            flexShrink: 0
                                                        }}
                                                    >
                                                        <InfoIcon
                                                            sx={{
                                                                width: '15px',
                                                                height: '15px',
                                                                fontSize: '15px',
                                                                color: 'inherit'
                                                            }}
                                                        />
                                                    </Box>
                                                </Tooltip>
										    </Box>
                                            <Divider
                                                sx={{
                                                    flexGrow: 1,
                                                    backgroundColor: '#009Bd8',
                                                    marginLeft: 21
                                                }}
                                            />
                                        </Box>
									</Grid>
									<Grid item xs={12}>
										<FormControl fullWidth>
											<Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 2.5, alignItems: 'center' }}>
												<FormControlLabel
													label="All"
													sx={{
														marginRight: 0,
														whiteSpace: 'nowrap',
														'& .MuiFormControlLabel-label': {
															color: `${colors.textLight.main} !important`,
															fontSize: '13px'
														}
													}}
													control={
														<Checkbox
															checked={
                                                                sectorList.length > 0 &&
                                                                sectorList.every(
                                                                    (sector) =>
                                                                    formData.Sectors.includes(
                                                                        sector.Sector_Name
                                                                    )
                                                                )
                                                            }
															onChange={(event) => {
                                                                const availableSectors =
                                                                    sectorList.map(
                                                                    (sector) =>
                                                                        sector.Sector_Name
                                                                    );

                                                                setFormData({
                                                                    ...formData,
                                                                    Sectors: event.target.checked
                                                                    ? availableSectors
                                                                    : []
                                                                });
                                                            }}
															sx={{
																padding: '4px 6px',
																'& .MuiSvgIcon-root': {
																	color: `${colors.dropdown.checkBox} !important`
																}
															}}
														/>
													}
												/>
												{loading.sector
  ? 'Loading...'
  : sectorList.map((sector) => (
      <FormControlLabel
        key={sector.Sector_Id}
        label={sector.Sector_Name}
        sx={{
          marginRight: 0,
          whiteSpace: 'nowrap',
          '& .MuiFormControlLabel-label': {
            color:
              `${colors.textLight.main} !important`,
            fontSize: '13px'
          }
        }}
        control={
          <Checkbox
            checked={
              formData.Sectors.includes(
                sector.Sector_Name
              )
            }
            onChange={(event) => {
              handleChange(
                event,
                sector.Sector_Name,
                'Sectors'
              );
            }}
            sx={{
              padding: '4px 6px',
              '& .MuiSvgIcon-root': {
                color:
                  `${colors.dropdown.checkBox} !important`
              }
            }}
          />
        }
      />
    ))}
											</Box>
										</FormControl>
									</Grid>
									<Grid item xs={12}>
										<Box
											sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '16px', mt: '16px' }}
										>
											<Typography
												variant="subHeader"
												color="dark.main"
												style={{
													color: '#009BD8',
													display: 'inline-block',
                                                    fontWeight:600,
													paddingRight: '20px',
													position: 'absolute',
													zIndex: 2
												}}
											>
												Project Details
											</Typography>
                                            <Divider sx={{ flexGrow: 1, backgroundColor: '#009BD8', marginLeft: 19 }} />
										</Box>
									</Grid>
									<Grid item xs={6}>
										<FormControl fullWidth>
											<CustomAutocomplete
												textFieldLabel={getLabel()}
												loading={dropdownLoading.project}
												options={projectOptionList}
												open={open && programData.action !== 'EDIT'}
												onOpen={() => setOpen(true)}
												onClose={() => setOpen(false)}
												value={formData.projectName || ''}
                                                disabled={programData.action == 'EDIT' && isClarityProject}
												onInputChange={(e, val) => {
													if (val) {
														const trimmedValue = val.replace(/^\s+/, '');
														handleInputChange(e, 'projectName', trimmedValue);
													}
												}}
												onChange={(e, val) => handleChange(e, val, 'projectName')}
												noOptionsText={
													<Typography
														sx={{ cursor: 'pointer', color: `${colors.input.text} !important` }}
														onClick={() => {
															onCreatenewPrj({ target: { checked: true } });
														}}
													>
														No project found <br />
														Create New Project
													</Typography>
												}
												sx={{
													...getInputCustomCommonSx(colors),
													'& .MuiInputBase-input': { color: '#71758F !important', WebkitTextFillColor: '#71758F !important', fontSize: '14px !important' },
													'& .MuiInputLabel-root': { color: '#71758F !important', fontSize: '14px !important' },
													'& .MuiInputLabel-root.Mui-focused': { color: '#71758F !important', fontSize: '13px !important' },
													'& .MuiInputLabel-root.MuiInputLabel-shrink': { color: '#71758F !important', fontSize: '13px !important' },
													'& .MuiFormLabel-root': { color: '#71758F !important' },
													'& .MuiAutocomplete-input': { color: '#71758F !important', WebkitTextFillColor: '#71758F !important', fontSize: '14px !important' }
												}}
												componentsProps={getAutocompleteCommonComponentsProps(colors)}
												customTextFieldProps={{
													InputLabelProps: {
														sx: {
															'&&': { color: '#71758F !important', fontSize: '14px !important' },
															'&.Mui-focused': { color: '#71758F !important', fontSize: '13px !important' },
															'&.MuiInputLabel-shrink': { color: '#71758F !important', fontSize: '13px !important' }
														}
													},
													InputProps: {
														sx: {
															'&&': { color: '#71758F !important' },
															'& input': { color: '#71758F !important', WebkitTextFillColor: '#71758F !important', fontSize: '14px !important' }
														}
													},
													sx: {
														'& .MuiOutlinedInput-root': {
															'& fieldset': { borderColor: `${colors.input.border} !important` },
															'&:hover fieldset': { borderColor: `${colors.input.borderFocus} !important` },
															'&.Mui-focused fieldset': { borderColor: `${colors.input.borderFocus} !important` }
														},
														'& .MuiInputLabel-root': {
															color: '#71758F !important',
															fontSize: '14px !important'
														},
														'& .MuiInputLabel-root.Mui-focused': {
															color: '#71758F !important',
															fontSize: '13px !important'
														},
														'& .MuiInputLabel-root.MuiInputLabel-shrink': {
															color: '#71758F !important',
															fontSize: '13px !important'
														}
													}
												}}
											/>
										</FormControl>
									</Grid>
									<Grid item xs={6}>
										<CustomTextField
											id="FriendlyName"
											label={
												<Box display="flex" alignItems="center" sx={{ fontSize: '14px' }}>
													Alias
													<Typography variant="body2" sx={{ marginLeft: 1, fontSize: 'inherit' }}>
														(Friendly Name)
													</Typography>
												</Box>
											}
											name="friendlyName"
											variant="outlined"
											autoComplete="off"
											value={formData.friendlyName}
											error={formData.friendlyName?.length > 45}
											helperText="Friendly Name can't contain more that 45 characters."
											onChange={(e) => handleChange(e, '', 'friendlyName')}
											onBlur={(e) => handleOnBlur(e, 'friendlyName')}
											onKeyDown={validateAliasInput}
											onPaste={handlePaste}
											sx={commonInputSx}
										/>
									</Grid>

									{getProjectOwnerField({
                                        isClarityProject,
                                        programData,
                                        formData,
                                        handleChange,
                                        handleInputChange,
                                        ownerList,
                                        isDisabled,
                                        dropdownLoading,
                                        colors
                                    })}

									<Grid item xs={6}>
										<CustomTextField
											id="ProjectDescription"
											label="Description"
											name="projectDescription"
											variant="outlined"
											autoComplete="off"
											disabled={isDisabled.description || isClarityProject}
											value={formData.projectDescription}
											onChange={handleChange}
											sx={commonInputSx}
										/>
									</Grid>
								</Grid>
							</DialogContent>
                                    
							<DialogActions>
								<Button
									onClick={(e) => handleCancl('close')}
									variant="pepwiseOutline"
									sx={{ color: `${colors.buttons.primary.color} !important` }}
								>
									CANCEL
								</Button>
                                {(() => {
                                    let buttonText = 'ADD PROJECT';
                                    if (saveBtnLoader) {
                                        buttonText = 'SAVING...';
                                    } else if (programData.action === 'EDIT') {
                                        buttonText = 'UPDATE & SAVE';
                                    }
                                    return (
                                        <Button disabled={!isProjectFormValid()} type="submit" variant="pepwiseSecondary">
                                            {buttonText}
                                        </Button>
                                    );
                                })()}
							</DialogActions>
						</Box>         
						<Box p={2} sx={{ backgroundColor: `${colors.modal.bg} !important`, color: `${colors.modal.text} !important` }}>
							<Dialog open={openModal.isOpen} onClose={() => handleClose('close')}>
								<DialogContent>
									<Stack direction="row" spacing={2} alignItems="center">
										<Box component="img" src={WarningImage} alt="" sx={{ width: '100px', height: '100px', flexShrink: 0 }} />
										<Stack spacing={2}>
											<Typography fontSize="28px" fontWeight={600}>
												Are you sure to delete {deleteMessage}
											</Typography>
											<Typography variant="body1">Once confirmed, the action can't be undone.</Typography>
										</Stack>
									</Stack>
								</DialogContent>

								<DialogActions>
									<Button onClick={(e) => handleClose('close')} variant="pepwiseOutline" sx={{ color: `${colors.buttons.primary.color} !important` }}>
										NO, I WANT IT!
									</Button>
									<Button onClick={onSubmit} variant="pepwiseError">
										{saveBtnLoader ? 'DELETING...' : 'DELETE'}
									</Button>
								</DialogActions>
							</Dialog>
						</Box>
					</Grid>
				</Grid>
				<Box p={3} pr={2} mt={1}>
                    <Divider sx={{ flexGrow: 1, backgroundColor: 'rgba(0, 155, 216, 1)' }} />
					<Box sx={{ position: 'relative' }}>
						<Typography
							mt={3}
							variant="widgetTitle"
							sx={{ position: 'absolute', color: `${colors.textLight.main} !important` }}
							component="span"
						>
							{' '}
							Projects
						</Typography>
						{programDetail?.ProjectCount > 0 && (
							<CustomTextField
								placeholder="Search Projects"
								value={searchProject}
								onChange={(e) => onProjectSearch(e.target.value)}
								style={{
									width: '22rem',
									position: 'absolute',
									top: '11px',
									right: '70px',
									backgroundColor: '#F4F7FA',
									borderRadius: '4px',
									zIndex: '99'
								}}
								sx={{
									'& .MuiOutlinedInput-root': {
										backgroundColor: '#F4F7FA !important',
										borderRadius: '4px',
										'& fieldset': {
											borderColor: 'rgba(113, 117, 143, 0.3) !important'
										},
										'&:hover fieldset': {
											borderColor: 'rgba(113, 117, 143, 0.5) !important'
										},
										'&.Mui-focused fieldset': {
											borderColor: 'rgba(0, 155, 216, 1) !important'
										}
									},
									'& .MuiInputBase-input': {
										color: '#71758F !important',
										WebkitTextFillColor: '#71758F !important',
										fontSize: '13px !important',
										padding: '8px 10px !important'
									},
									'& .MuiInputBase-input::placeholder': {
										color: '#71758F !important',
										opacity: '1 !important'
									}
								}}
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<Search sx={{ color: '#71758F !important', fontSize: '18px' }} />
										</InputAdornment>
									)
								}}
							/>
						)}
					</Box>
					{content}
				</Box>
			</Box>
		);
}